import constapp from "@/plugins/const";

/**
 * Servicio unificado de comunicación en tiempo real para Firma Digital y Sistema de Turnos en Terminals
 * Soporta Hubs SignalR de SunExpert ('documentSigningHub' y 'notification')
 */
class DocumentSigningSignalrService {
  constructor() {
    this.currentScreenId = "";
    this.kioskCallbacks = null;
    this.operatorCallbacks = null;
    this.displayCallbacks = null;
    this.onlineKiosksMap = {}; // KioskId -> { isBusy, lastSeen }
    this.initPromise = null;

    // Escuchar eventos globales de SignalR
    if (typeof window !== "undefined") {
      window.addEventListener("signalr-message-group", (event) => {
        const { message, group } = (event && event.detail) || {};
        if (!message) return;
        this.processIncomingMessage(message, group);
      });
    }
  }

  async ensureSignalR() {
    if (typeof window === "undefined") return false;
    const $ = window.$ || window.jQuery;
    if (!$) return false;

    if (this.getHub() && $.connection && $.connection.hub) {
      return true;
    }

    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve) => {
      try {
        require("signalr");
      } catch (e) {
        console.warn("SignalR library require error:", e);
      }

      const backendUrl = (constapp.URL || "http://localhost:5005/").replace(/\/+$/, "");
      const scriptId = "signalr-hubs-script";
      let existingScript = document.getElementById(scriptId);

      const onLoaded = () => {
        if ($.connection && $.connection.hub) {
          $.connection.hub.url = backendUrl + "/signalr";
        }
        resolve(true);
      };

      if (!existingScript) {
        const script = document.createElement("script");
        script.id = scriptId;
        script.src = backendUrl + "/signalr/hubs";
        script.onload = onLoaded;
        script.onerror = () => {
          console.warn("No se pudo cargar /signalr/hubs dinámicamente");
          resolve(false);
        };
        document.head.appendChild(script);
      } else {
        onLoaded();
      }
    });

    return this.initPromise;
  }

  getHub() {
    const $ = window.$ || window.jQuery;
    if (!$ || !$.connection) return null;
    return $.connection.documentSigningHub || $.connection.notification || $.connection.notificationHub;
  }

  getNotifHub() {
    const $ = window.$ || window.jQuery;
    if (!$ || !$.connection) return null;
    return $.connection.notification || $.connection.notificationHub;
  }

  broadcastToGroup(groupName, payload) {
    const notifHub = this.getNotifHub();
    if (notifHub) {
      const msgStr = typeof payload === "string" ? payload : JSON.stringify(payload);
      if (notifHub.server && typeof notifHub.server.sendMessageGroup === "function") {
        notifHub.server.sendMessageGroup(msgStr, groupName);
      } else if (typeof notifHub.invoke === "function") {
        notifHub.invoke("sendMessageGroup", msgStr, groupName);
      }
    }
  }

  processIncomingMessage(msg, group) {
    if (!msg) return;

    let payload = msg;
    if (typeof msg === "string") {
      try { payload = JSON.parse(msg); } catch (e) { }
    }

    // 0. Presencia de Terminales (Peer-to-peer / Hub)
    if (payload.type === "PING_KIOSKS") {
      if (this.currentScreenId) {
        this.broadcastToGroup("OPERATORS", {
          type: "KIOSK_ONLINE",
          KioskId: this.currentScreenId,
          IsBusy: false,
        });
      }
    } else if (payload.type === "KIOSK_ONLINE" && payload.KioskId) {
      this.onlineKiosksMap[payload.KioskId] = {
        KioskId: payload.KioskId,
        IsBusy: !!payload.IsBusy,
        lastSeen: Date.now(),
      };
      if (this.operatorCallbacks && typeof this.operatorCallbacks.onQueueUpdated === "function") {
        const activeKiosks = Object.values(this.onlineKiosksMap);
        this.operatorCallbacks.onQueueUpdated({ ActiveKiosks: activeKiosks });
      }
    }

    // 1. Turno asignado a este Terminal
    if (payload.type === "TURN_ASSIGNED" || payload.Turn || (payload.turn && payload.turn.AssignedKiosk)) {
      const inner = payload.payload || payload;
      const data = inner.Turn || inner.turn || inner;
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onTurnAssigned === "function") {
        this.kioskCallbacks.onTurnAssigned(data);
      }
    }
    // 2. Turno llamado (Para Display TV de sala)
    else if (payload.type === "TURN_CALLED" || (payload.TurnCode && payload.KioskId) || (payload.payload && payload.payload.TurnCode)) {
      const data = payload.payload || payload;
      if (this.displayCallbacks && typeof this.displayCallbacks.onTurnCalled === "function") {
        this.displayCallbacks.onTurnCalled(data);
      }
    }
    // 3. Actualización global de la cola
    else if (payload.type === "QUEUE_UPDATED" || (payload.ActiveCalls && payload.WaitingQueue)) {
      const data = payload.payload || payload;
      if (this.displayCallbacks && typeof this.displayCallbacks.onQueueUpdated === "function") {
        this.displayCallbacks.onQueueUpdated(data);
      }
      if (this.operatorCallbacks && typeof this.operatorCallbacks.onQueueUpdated === "function") {
        this.operatorCallbacks.onQueueUpdated(data);
      }
    }
    // 4. Colaborador completó revisión de documentos (Listo para Operador)
    else if (payload.type === "TURN_READY_FOR_OPERATOR" || payload.Status === "DOCUMENTS_COMPLETED") {
      if (this.operatorCallbacks && typeof this.operatorCallbacks.onTurnReadyForOperator === "function") {
        this.operatorCallbacks.onTurnReadyForOperator(payload);
      }
    }
    // 5. Extensión de tiempo de lectura
    else if (payload.type === "READING_EXTENDED" || payload.ExtraMinutes) {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onReadingExtended === "function") {
        this.kioskCallbacks.onReadingExtended(payload);
      }
    }
    // 6. Reset o expiración de Terminal
    else if (payload.type === "KIOSK_RESET") {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onKioskReset === "function") {
        this.kioskCallbacks.onKioskReset(payload);
      }
    } else if (payload.type === "KIOSK_EXPIRED_MESSAGE") {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onKioskExpiredMessage === "function") {
        this.kioskCallbacks.onKioskExpiredMessage(payload);
      }
    } else if (payload.type === "KIOSK_COMPLETE_SUCCESS") {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onKioskCompleteSuccess === "function") {
        this.kioskCallbacks.onKioskCompleteSuccess(payload);
      }
    } else if (payload.type === "CONTRACT_FILES_GENERATED") {
      const inner = payload.payload || payload;
      if (this.operatorCallbacks && typeof this.operatorCallbacks.onContractFilesGenerated === "function") {
        this.operatorCallbacks.onContractFilesGenerated(inner);
      }
    }
  }

  setupHubClient(hub) {
    if (!hub || !hub.client) return;

    hub.client.OnTurnAssigned = (data) => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onTurnAssigned === "function") {
        this.kioskCallbacks.onTurnAssigned(data);
      }
    };
    hub.client.onTurnAssigned = hub.client.OnTurnAssigned;

    hub.client.OnTurnCalled = (data) => {
      if (this.displayCallbacks && typeof this.displayCallbacks.onTurnCalled === "function") {
        this.displayCallbacks.onTurnCalled(data);
      }
    };
    hub.client.onTurnCalled = hub.client.OnTurnCalled;

    hub.client.OnQueueUpdated = (data) => {
      if (this.displayCallbacks && typeof this.displayCallbacks.onQueueUpdated === "function") {
        this.displayCallbacks.onQueueUpdated(data);
      }
      if (this.operatorCallbacks && typeof this.operatorCallbacks.onQueueUpdated === "function") {
        this.operatorCallbacks.onQueueUpdated(data);
      }
    };
    hub.client.onQueueUpdated = hub.client.OnQueueUpdated;

    hub.client.OnTurnReadyForOperator = (data) => {
      if (this.operatorCallbacks && typeof this.operatorCallbacks.onTurnReadyForOperator === "function") {
        this.operatorCallbacks.onTurnReadyForOperator(data);
      }
    };
    hub.client.onTurnReadyForOperator = hub.client.OnTurnReadyForOperator;

    hub.client.OnReadingExtended = (data) => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onReadingExtended === "function") {
        this.kioskCallbacks.onReadingExtended(data);
      }
    };
    hub.client.onReadingExtended = hub.client.OnReadingExtended;

    hub.client.OnKioskReset = () => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onKioskReset === "function") {
        this.kioskCallbacks.onKioskReset();
      }
    };
    hub.client.onKioskReset = hub.client.OnKioskReset;

    hub.client.OnKioskExpiredMessage = (data) => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onKioskExpiredMessage === "function") {
        this.kioskCallbacks.onKioskExpiredMessage(data);
      }
    };
    hub.client.onKioskExpiredMessage = hub.client.OnKioskExpiredMessage;

    hub.client.OnKioskCompleteSuccess = (data) => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onKioskCompleteSuccess === "function") {
        this.kioskCallbacks.onKioskCompleteSuccess(data);
      }
    };
    hub.client.onKioskCompleteSuccess = hub.client.OnKioskCompleteSuccess;

    hub.client.OnSessionReplaced = (msg) => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onSessionReplaced === "function") {
        this.kioskCallbacks.onSessionReplaced(msg);
      }
    };
    hub.client.onSessionReplaced = hub.client.OnSessionReplaced;

    hub.client.OnKioskAlreadyInUse = (msg) => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onKioskAlreadyInUse === "function") {
        this.kioskCallbacks.onKioskAlreadyInUse(msg);
      }
    };
    hub.client.onKioskAlreadyInUse = hub.client.OnKioskAlreadyInUse;

    hub.client.OnKioskRegisteredSuccess = (kioskId) => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onKioskRegisteredSuccess === "function") {
        this.kioskCallbacks.onKioskRegisteredSuccess(kioskId);
      }
    };
    hub.client.onKioskRegisteredSuccess = hub.client.OnKioskRegisteredSuccess;

    hub.client.OnQueueUpdated = (data) => {
      if (this.kioskCallbacks && typeof this.kioskCallbacks.onQueueUpdated === "function") {
        this.kioskCallbacks.onQueueUpdated(data);
      }
      if (this.displayCallbacks && typeof this.displayCallbacks.onQueueUpdated === "function") {
        this.displayCallbacks.onQueueUpdated(data);
      }
      if (this.operatorCallbacks && typeof this.operatorCallbacks.onQueueUpdated === "function") {
        this.operatorCallbacks.onQueueUpdated(data);
      }
      try {
        window.dispatchEvent(new CustomEvent("sunexpert-queue-updated", { detail: data }));
      } catch (e) { }
    };
    hub.client.onQueueUpdated = hub.client.OnQueueUpdated;
  }

  // --- CONEXIÓN DE Terminal ---
  async connectKiosk(screenId, callbacks = {}, tabSessionId = null, forceTakeover = false) {
    this.currentScreenId = String(screenId || "").trim().toUpperCase();
    this.kioskCallbacks = callbacks;

    await this.ensureSignalR();

    const $ = window.$ || window.jQuery;
    if (!$ || !$.connection) return false;

    const hub = this.getHub();
    if (!hub) return false;

    this.setupHubClient(hub);

    // Configurar reconexión automática resiliente
    if (!this._reconnectedHandlerAdded && $.connection.hub) {
      this._reconnectedHandlerAdded = true;
      $.connection.hub.reconnecting(() => {
        console.warn("🖥️ [SignalR] Reconectando transporte...");
      });
      $.connection.hub.reconnected(() => {
        console.log("🖥️ [SignalR] Reconectado al servidor exitosamente.");
        if (this.currentScreenId) {
          const tabId = sessionStorage.getItem("SUNEXPERT_TAB_SESSION_ID");
          const h = this.getHub();
          if (h && h.server && typeof h.server.registerScreen === "function") {
            h.server.registerScreen(this.currentScreenId, tabId, false);
          }
          this.joinGroupDirect(this.currentScreenId);
          this.joinGroupDirect("ALL_KIOSKS");
        }
      });
      $.connection.hub.disconnected(() => {
        console.warn("🖥️ [SignalR] Desconectado. Reintentando iniciar en 3s...");
        setTimeout(() => {
          if ($.connection.hub && $.connection.hub.state === 4) {
            $.connection.hub
              .start()
              .done(() => {
                console.log("🖥️ [SignalR] Conexión restablecida con éxito.");
                if (this.currentScreenId) {
                  const tabId = sessionStorage.getItem(
                    "SUNEXPERT_TAB_SESSION_ID",
                  );
                  const h = this.getHub();
                  if (
                    h &&
                    h.server &&
                    typeof h.server.registerScreen === "function"
                  ) {
                    h.server.registerScreen(this.currentScreenId, tabId, false);
                  }
                  this.joinGroupDirect(this.currentScreenId);
                  this.joinGroupDirect("ALL_KIOSKS");
                }
              })
              .fail(() => {});
          }
        }, 3000);
      });
    }

    const register = () => {
      if (hub.server && typeof hub.server.registerScreen === "function") {
        hub.server.registerScreen(
          this.currentScreenId,
          tabSessionId,
          forceTakeover,
        );
      } else if (typeof hub.invoke === "function") {
        hub.invoke(
          "registerScreen",
          this.currentScreenId,
          tabSessionId,
          forceTakeover,
        );
      }
      this.joinGroupDirect(this.currentScreenId);
      this.joinGroupDirect("ALL_KIOSKS");

      // Anunciar a operadores que este Terminal está online
      this.broadcastToGroup("OPERATORS", {
        type: "KIOSK_ONLINE",
        KioskId: this.currentScreenId,
        IsBusy: false,
      });
    };

    if ($.connection.hub.state === 1) {
      register();
      return true;
    } else {
      return new Promise((resolve) => {
        $.connection.hub
          .start()
          .done(() => {
            register();
            resolve(true);
          })
          .fail(() => {
            console.warn("Error al iniciar SignalR hub en connectKiosk");
            resolve(false);
          });
      });
    }
  }

  kioskHeartbeat(screenId, tabSessionId) {
    const $ = window.$ || window.jQuery;
    if (
      !$ ||
      !$.connection ||
      !$.connection.hub ||
      $.connection.hub.state !== 1
    ) {
      return;
    }
    const hub = this.getHub();
    if (!hub) return;
    const normalized = String(screenId || "")
      .trim()
      .toUpperCase();
    try {
      if (hub.server && typeof hub.server.kioskHeartbeat === "function") {
        hub.server.kioskHeartbeat(normalized, tabSessionId);
      } else if (typeof hub.invoke === "function") {
        hub.invoke("kioskHeartbeat", normalized, tabSessionId);
      }
    } catch (e) {
      // Ignorar errores transitorios si el socket está reconectando
    }
  }

  // --- CONEXIÓN DE PANTALLA TV (TURNERO BANCARIO) ---
  async connectDisplayTV(callbacks = {}) {
    this.displayCallbacks = callbacks;

    await this.ensureSignalR();

    const $ = window.$ || window.jQuery;
    if (!$ || !$.connection) return false;

    const hub = this.getHub();
    if (!hub) return false;

    this.setupHubClient(hub);

    const register = () => {
      if (hub.server && typeof hub.server.registerDisplayTV === "function") {
        hub.server.registerDisplayTV();
      } else if (typeof hub.invoke === "function") {
        hub.invoke("registerDisplayTV");
      }
      this.joinGroupDirect("DISPLAY_TV");
    };

    if ($.connection.hub.state === 1) {
      register();
      return true;
    } else {
      return new Promise((resolve) => {
        $.connection.hub
          .start()
          .done(() => {
            register();
            resolve(true);
          })
          .fail(() => resolve(false));
      });
    }
  }

  disconnectDisplayTV() {
    this.displayCallbacks = {};
  }

  disconnectKiosk() {
    this.kioskCallbacks = {};
    this.currentScreenId = "";
  }

  disconnectOperator() {
    this.operatorCallbacks = {};
  }

  // --- CONEXIÓN DE OPERADOR DE RR.HH. ---
  async connectOperator(callbacks = {}) {
    this.operatorCallbacks = callbacks;

    await this.ensureSignalR();

    const $ = window.$ || window.jQuery;
    if (!$ || !$.connection) return false;

    const hub = this.getHub();
    if (!hub) return false;

    this.setupHubClient(hub);

    const register = () => {
      if (hub.server && typeof hub.server.registerOperator === "function") {
        hub.server.registerOperator();
      } else if (typeof hub.invoke === "function") {
        hub.invoke("registerOperator");
      }
      this.joinGroupDirect("OPERATORS");

      // Preguntar qué Terminals están online
      this.broadcastToGroup("ALL_KIOSKS", { type: "PING_KIOSKS" });
    };

    if ($.connection.hub.state === 1) {
      register();
      return true;
    } else {
      return new Promise((resolve) => {
        $.connection.hub
          .start()
          .done(() => {
            register();
            resolve(true);
          })
          .fail(() => resolve(false));
      });
    }
  }

  joinGroupDirect(groupName) {
    const $ = window.$ || window.jQuery;
    const notifHub = this.getNotifHub();
    if (notifHub) {
      if (notifHub.server && typeof notifHub.server.addToGroup === "function") {
        notifHub.server.addToGroup(groupName);
      } else if (typeof notifHub.invoke === "function") {
        notifHub.invoke("addToGroup", groupName);
      }
    }
  }

  // --- ACCIONES DESDE EL Terminal ---

  kioskStartReading(kioskId, turnId) {
    const hub = this.getHub();
    if (hub && hub.server && typeof hub.server.kioskStartReading === "function") {
      hub.server.kioskStartReading(kioskId, turnId);
    } else if (hub && typeof hub.invoke === "function") {
      hub.invoke("kioskStartReading", kioskId, turnId);
    }
  }

  kioskExtendReading(kioskId, turnId) {
    const hub = this.getHub();
    if (hub && hub.server && typeof hub.server.kioskExtendReading === "function") {
      hub.server.kioskExtendReading(kioskId, turnId);
    } else if (hub && typeof hub.invoke === "function") {
      hub.invoke("kioskExtendReading", kioskId, turnId);
    }
  }

  kioskNoShow(kioskId, turnId) {
    const hub = this.getHub();
    if (hub && hub.server && typeof hub.server.kioskNoShow === "function") {
      hub.server.kioskNoShow(kioskId, turnId);
    } else if (hub && typeof hub.invoke === "function") {
      hub.invoke("kioskNoShow", kioskId, turnId);
    }
  }

  kioskReadingExpired(kioskId, turnId) {
    const hub = this.getHub();
    if (hub && hub.server && typeof hub.server.kioskReadingExpired === "function") {
      hub.server.kioskReadingExpired(kioskId, turnId);
    } else if (hub && typeof hub.invoke === "function") {
      hub.invoke("kioskReadingExpired", kioskId, turnId);
    }
  }

  kioskCompleteDocuments(kioskId, turnId, auditLog) {
    const hub = this.getHub();
    if (hub && hub.server && typeof hub.server.kioskCompleteDocuments === "function") {
      hub.server.kioskCompleteDocuments(kioskId, turnId, auditLog);
    } else if (hub && typeof hub.invoke === "function") {
      hub.invoke("kioskCompleteDocuments", kioskId, turnId, auditLog);
    }
  }

  operatorFinishTurn(turnId) {
    const hub = this.getHub();
    if (hub && hub.server && typeof hub.server.operatorFinishTurn === "function") {
      hub.server.operatorFinishTurn(turnId);
    } else if (hub && typeof hub.invoke === "function") {
      hub.invoke("operatorFinishTurn", turnId);
    }
  }

  releaseKiosk(kioskId) {
    const hub = this.getHub();
    if (hub && hub.server && typeof hub.server.releaseKiosk === "function") {
      hub.server.releaseKiosk(kioskId);
    } else if (hub && typeof hub.invoke === "function") {
      hub.invoke("releaseKiosk", kioskId);
    }
  }
}

export default new DocumentSigningSignalrService();
